import * as React from 'react';
import { format } from 'date-fns';

// Material-UI
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import {
  DataGrid,
  GridCellParams,
  GridColDef
} from '@mui/x-data-grid';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

// Styles
import { useTheme } from '@mui/material/styles';

import {
  HTTPHeaders
} from '../../interfaces';

import {
  SignupPast
} from '../../models';

import {
  API_URL,
  EMPTY_STRING,
  NULL_ID
} from '../../global';

// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';

// UTC to local date formatting
import { useDates } from '../../hooks/UseDates';

// Responsive hooks
import { useResponsive } from '../../hooks/UseResponsive';

interface PastRequestProps {
  closeDrawer: () => void;
  signupID: number;
}

const PastRequest: React.FC<PastRequestProps> = (props) => {

  const { getAccessToken } = useAuth0User();
  const { dateStringToLocalDateFormatted, isNullDate } = useDates();
  const { isMobile } = useResponsive();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [signup, setSignup] = React.useState<SignupPast>(new SignupPast());

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      flexGrow: 1,
      padding: theme.spacing(2, 2, 2, 2)
    },
    divider: {
      margin: theme.spacing(1, 0, 1, 0)
    },
    sectionHeading: {
      margin: theme.spacing(2, 0, 1, 0)
    },
  };

  // #endregion

  // #region Methods

  /*
* Grid column definitions
*/
  const guestColumns = (): Array<GridColDef> => {
    const columns: Array<GridColDef> = [
      {
        field: 'lastName',
        headerName: 'Name',
        width: 150,
        type: 'string',
        renderCell: (params: GridCellParams) => renderName(params)
      },
      {
        field: 'arrivalDate',
        headerName: 'Dates',
        width: isMobile ? 100 : 125,
        type: 'string',
        renderCell: (params: GridCellParams) => renderDates(params)
      },
      {
        field: 'guestType',
        headerName: 'Type',
        width: 150,
        type: 'string',
      },
      {
        field: 'cancelDate',
        headerName: 'Cancelled',
        width: 200,
        type: 'string',
        renderCell: (params: GridCellParams) => renderCancelDate(params)
      }
    ];

    // Keep logic consistent: filter out the 'guestType' column on mobile
    return isMobile
      ? columns.filter(col => col.field !== 'guestType')
      : columns;
  };

  /*
* Grid column definitions
*/
  const memberColumns = (): Array<GridColDef> => {
    const columns: Array<GridColDef> = [
      {
        field: 'lastName',
        headerName: 'Name',
        width: 150,
        type: 'string',
        // Note: Simplified the arrow function return for brevity
        renderCell: (params: GridCellParams) => renderName(params)
      },
      {
        field: 'arrivalDate',
        headerName: 'Dates',
        width: isMobile ? 100 : 125,
        type: 'string',
        renderCell: (params: GridCellParams) => renderDates(params)
      },
      {
        field: 'memberStatus',
        headerName: 'Type',
        width: 150,
        type: 'string',
      },
      {
        field: 'cancelDate',
        headerName: 'Cancelled',
        width: 200,
        type: 'string',
        renderCell: (params: GridCellParams) => renderCancelDate(params)
      }
    ];

    // If on mobile, filter out the 'memberStatus' column by its field name
    return isMobile
      ? columns.filter(col => col.field !== 'memberStatus')
      : columns;
  };

  /*
   * Fetch signup data
   */
  const fetchSignup = async () => {

    setIsLoading(true);

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    const url = new URL(`${API_URL}/v1/signups/past/${props.signupID}`);

    await fetch(url.toString(),
      {
        method: 'GET',
        cache: 'no-cache',
        headers: headers,
        mode: 'cors'
      })
      .then((response) => {
        if (!response.ok) {
          setIsLoading(false);
          throw new Error('Past signups not retrieved');
        }
        return response.json()
      })
      .then((signup: SignupPast) => {
        setSignup(signup);
        setIsLoading(false);
      })
      .catch(err => {
        setIsLoading(false);
        throw new Error('Past signups not retrieved');
      }
      );
  }

  /*
  * Render cancel date
  */
  const renderCancelDate = (params: GridCellParams) => {
    return isNullDate(params.row.cancelDate) ? EMPTY_STRING : `${dateStringToLocalDateFormatted(params.row.cancelDate)}`;
  }

  /*
  * Render dates
  */
  const renderDates = (params: GridCellParams) => {
    return `${format(new Date(params.row.arrivalDate), 'MM/dd')}-${format(new Date(params.row.departureDate), 'MM/dd')}`
  }

  /*
* Render member or guest name
*/
  const renderName = (params: GridCellParams) => {
    return `${params.row.lastName}, ${params.row.firstName}`
  }

  // #endregion

  // #region Event handlers

  React.useEffect(() => {
    if ((props.signupID !== NULL_ID) && (signup.signupID !== props.signupID)) {
      fetchSignup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.signupID]);

  // #endregion

  return (
    <Box sx={styles.root}>
      <Grid container alignItems="center">
        {isMobile &&
          <Grid>
            <IconButton onClick={props.closeDrawer} size="large">
              <CloseIcon />
            </IconButton>
          </Grid>
        }
        <Grid>
          <Typography variant="h6">{`Past Request #${signup.referenceNumber}`}</Typography>
        </Grid>
      </Grid>
      {isMobile &&
        <Divider sx={styles.divider} />
      }
      <Grid container>
        <Typography variant="body1" gutterBottom>{`${signup.minArrivalDate}-${signup.maxDepartureDate} Nights:${signup.nights}`}</Typography>
      </Grid>
      {signup.memberSignups.length > 0 &&
        <>
          <Typography variant="body1" sx={styles.sectionHeading}>Household Members</Typography>
          <DataGrid
            columns={memberColumns()}
            columnVisibilityModel={{
              guestType: !isMobile,
              memberStatus: !isMobile
            }}
            density="compact"
            disableColumnSelector
            disableRowSelectionOnClick
            getRowId={(row) => row.memberSignupID} // Unique ID for each row
            hideFooter
            loading={isLoading}
            rows={signup.memberSignups}
            sx={{
              ...styles.root,
              height: 'auto', // Explicitly tell the Grid its height is auto
              '& .MuiDataGrid-main': { height: 'auto' } // Ensure the internal viewport follows suit
            }}
        />
        </>
      }
      {signup.guestSignups.length > 0 &&
        <>
          <Typography variant="body1" sx={styles.sectionHeading}>Guests</Typography>
          <DataGrid
            columns={guestColumns()}
            columnVisibilityModel={{
              guestType : !isMobile,
              memberStatus: !isMobile
            }}
            density="compact"
            disableColumnSelector
            disableRowSelectionOnClick
            getRowId={(row) => row.guestSignupID} // Unique ID for each row
            hideFooter
            loading={isLoading}
            rows={signup.guestSignups}
            sx={{
              ...styles.root,
              height: 'auto', // Explicitly tell the Grid its height is auto
              '& .MuiDataGrid-main': { height: 'auto' } // Ensure the internal viewport follows suit
            }}
        />
        </>
      }
    </Box>
  );

}

export default PastRequest;